import { zodResolver } from "@hookform/resolvers/zod";
import { type MeResponse, updateProfileRequestSchema } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  cn,
  FormField,
  Input,
  PageHeader,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Monitor, Moon, Sun } from "lucide-react";
import { type ReactElement, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { z } from "zod";
import { api } from "../../../lib/api.js";
import { applyFieldErrors, errorMessage } from "../../../lib/errors.js";
import { useDocumentTitle, useDraftGuard, useSubmitShortcut } from "../../../lib/hooks.js";
import { setPreferences, type ThemeMode, usePreferences } from "../../../lib/preferences.js";
import { queryKeys } from "../../../lib/query.js";
import { useMe } from "../../../lib/session.js";

export const Route = createFileRoute("/_app/ayarlar/profil")({ component: ProfilePage });

function ProfilePage() {
  const { t } = useTranslation();
  const me = useMe().data;
  useDocumentTitle(t("menu.profile"));
  if (!me) return <SkeletonRows rows={5} />;
  return (
    <>
      <PageHeader
        title={t("menu.profile")}
        description={t("profile.description")}
        breadcrumb={[{ label: t("menu.groups.settings") }, { label: t("menu.profile") }]}
      />
      <div className="grid max-w-3xl gap-4">
        <ProfileForm me={me} />
        <AppearanceCard />
        <MembershipsCard me={me} />
      </div>
    </>
  );
}

type ProfileValues = z.input<typeof updateProfileRequestSchema>;

function ProfileForm({ me }: { me: MeResponse }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const formRef = useRef<HTMLFormElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<ProfileValues>({
    resolver: zodResolver(updateProfileRequestSchema),
    defaultValues: { fullName: me.user.fullName, phone: me.user.phone ?? "" },
  });
  const { errors, isDirty, isSubmitting } = form.formState;
  useDraftGuard(isDirty && !isSubmitting);
  useSubmitShortcut(formRef);

  const submit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await api.PATCH("/me", {
        body: { fullName: values.fullName, phone: values.phone ? values.phone : null },
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.me });
      form.reset(values);
      toast({ tone: "success", title: t("common.saved") });
    } catch (error) {
      if (
        applyFieldErrors(error, form.setError, ["fullName", "phone"]).length > 0 ||
        Object.keys(form.formState.errors).length === 0
      )
        setFormError(errorMessage(error));
    }
  });

  return (
    <Card className="p-5 sm:p-6">
      <h2 className="mb-4 text-base font-semibold">{t("profile.personal")}</h2>
      <form ref={formRef} onSubmit={(event) => void submit(event)} className="grid max-w-md gap-4" noValidate>
        {formError ? <Alert tone="danger">{formError}</Alert> : null}
        <FormField label={t("auth.email")} description={t("profile.emailHint")}>
          {(control) => <Input {...control} value={me.user.email} readOnly disabled />}
        </FormField>
        <FormField label={t("auth.fullName")} error={errors.fullName?.message} required>
          {(control) => <Input {...control} {...form.register("fullName")} autoComplete="name" />}
        </FormField>
        <FormField label={t("common.phone")} error={errors.phone?.message}>
          {(control) => (
            <Input
              {...control}
              {...form.register("phone")}
              type="tel"
              autoComplete="tel"
              placeholder="05XX XXX XX XX"
            />
          )}
        </FormField>
        <Button type="submit" className="justify-self-start" loading={isSubmitting} disabled={!isDirty}>
          {t("common.save")}
        </Button>
      </form>
    </Card>
  );
}

const THEMES: Array<{ mode: ThemeMode; icon: typeof Sun }> = [
  { mode: "light", icon: Sun },
  { mode: "dark", icon: Moon },
  { mode: "system", icon: Monitor },
];

/** Görünüm tercihleri cihazda saklanır (hesaba değil): aynı kullanıcı ofiste açık, sahada koyu tema kullanabilir. */
function AppearanceCard(): ReactElement {
  const { t } = useTranslation();
  const preferences = usePreferences();
  return (
    <Card className="p-5 sm:p-6">
      <h2 className="text-base font-semibold">{t("profile.appearance")}</h2>
      <p className="mb-4 text-sm text-muted-foreground">{t("profile.appearanceHint")}</p>
      <fieldset>
        <legend className="mb-2 text-sm font-medium">{t("profile.theme")}</legend>
        <div className="flex flex-wrap gap-2">
          {THEMES.map(({ mode, icon: Icon }) => (
            <label
              key={mode}
              className={cn(
                "flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-ring",
                preferences.theme === mode && "border-primary bg-primary/10",
              )}
            >
              <input
                type="radio"
                name="theme"
                value={mode}
                checked={preferences.theme === mode}
                onChange={() => setPreferences({ theme: mode })}
                className="sr-only"
              />
              <Icon className="size-4" aria-hidden="true" />
              {t(`profile.themes.${mode}`)}
            </label>
          ))}
        </div>
      </fieldset>
      <label className="mt-5 flex items-start gap-3 text-sm">
        <Checkbox
          className="mt-0.5"
          checked={preferences.highContrast}
          onChange={(event) => setPreferences({ highContrast: event.target.checked })}
        />
        <span className="grid gap-0.5">
          <span className="font-medium">{t("profile.highContrast")}</span>
          <span className="text-muted-foreground">{t("profile.highContrastHint")}</span>
        </span>
      </label>
    </Card>
  );
}

function MembershipsCard({ me }: { me: MeResponse }): ReactElement | null {
  const { t } = useTranslation();
  if (me.memberships.length === 0) return null;
  return (
    <Card className="p-5 sm:p-6">
      <h2 className="mb-4 text-base font-semibold">{t("profile.memberships")}</h2>
      <ul className="grid gap-2">
        {me.memberships.map((membership) => (
          <li
            key={membership.tenantId}
            className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-4 py-3 text-sm"
          >
            <span className="font-medium">{membership.tenantName}</span>
            <span className="flex flex-wrap items-center gap-2">
              {membership.tenantId === me.activeTenant?.id
                ? me.activeTenant.assignments.map((assignment, index) => (
                    <StatusBadge key={`${assignment.roleId}-${index}`}>{assignment.roleName}</StatusBadge>
                  ))
                : null}
              {membership.tenantId === me.activeTenant?.id ? (
                <StatusBadge tone="success">{t("shell.tenantSwitcher.current")}</StatusBadge>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
